import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Globe, Link as LinkIcon, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getSegmentColorBgClass } from "@/constants/segmentColors";
import type { Segment } from "@/types/spyserp";
import { formatSegmentRulesSummary } from "@/components/spy-dashboard/segments/segmentRulesSummary";

interface SegmentListProps {
  segments: Segment[];
  isLoading: boolean;
  onEdit: (segment: Segment) => void;
  onDelete: (id: string) => void;
}

export const SegmentList = ({
  segments,
  isLoading,
  onEdit,
  onDelete,
}: SegmentListProps) => (
  <div className="border rounded-md bg-card">
    <Table>
      <TableHeader>
        <TableRow className="bg-muted/50">
          <TableHead>Segment Name</TableHead>
          <TableHead>Target Type</TableHead>
          <TableHead>Rules Summary</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell
              colSpan={4}
              className="text-center py-10 text-muted-foreground"
            >
              Loading segments...
            </TableCell>
          </TableRow>
        ) : segments.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={4}
              className="text-center py-10 text-muted-foreground"
            >
              No segments created yet.
            </TableCell>
          </TableRow>
        ) : (
          segments.map((seg) => (
            <TableRow key={seg.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "w-3 h-3 rounded-full",
                      getSegmentColorBgClass(seg.color),
                    )}
                  />
                  {seg.name}
                </div>
              </TableCell>
              <TableCell>
                {seg.target_type === "domain" ? (
                  <Badge variant="outline" className="gap-1">
                    <Globe className="w-3 h-3" /> Domain
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1">
                    <LinkIcon className="w-3 h-3" /> URL
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatSegmentRulesSummary(seg.rules)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(seg)}
                  className="cursor-pointer"
                  aria-label={`Edit ${seg.name}`}
                >
                  <Edit2 className="h-4 w-4 text-primary" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(seg.id)}
                  className="cursor-pointer"
                  aria-label={`Delete ${seg.name}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);
